import { z } from "zod";
import {
  WORKBOOK_PREVIEW_ROWS,
  WORKBOOK_RUN_STEP_TIMEOUT,
  WORKBOOK_VALIDATION_KINDS,
  workbookColumnTypeSchema,
  workbookValidationSeveritySchema,
} from "@erp/shared-types";
import { Action, Subject } from "@erp/authz";
import { defineStepConfig, defineToolConfig } from "../../registry/helpers";

/** Rows keyed by column name; one object is a one-row table. */
const parameterRowsSchema = z.union([
  z.record(z.string(), z.unknown()),
  z.array(z.record(z.string(), z.unknown())),
]);

export const workbookParametersSchema = z
  .record(z.string(), parameterRowsSchema)
  .describe(
    "Parameters: rows replacing the Workbook's inline tables for this run only, keyed by the inline table's name (ignoring case). Each value is an array of rows or one row, each row an object of column name to text, number or TRUE/FALSE. Other data is read as the Workbook reads it.",
  );

/** The most Result rows a Run Workbook node may carry in its output (`includeSampleRows`). */
export const RUN_WORKBOOK_MAX_SAMPLE_ROWS = 10;

/**
 * How a Worksheet's Validations fared: `failed` when an error-severity one
 * has failing rows, `warning` when only warnings do, `none` without any.
 */
const workbookResultValidationSchema = z.object({
  status: z.enum(["none", "passed", "warning", "failed"]),
  failed: z
    .array(
      z.object({
        validationId: z.uuid(),
        column: z.string(),
        kind: z.enum(WORKBOOK_VALIDATION_KINDS),
        severity: workbookValidationSeveritySchema,
        failedRowCount: z.number().int(),
      }),
    )
    .describe("The Validations with failing rows"),
});

const workbookRunResultSchema = z.object({
  sheetId: z.uuid(),
  name: z.string().describe("The Worksheet's name"),
  rowCount: z.number().int().describe("Rows in the whole Result"),
  errorCount: z.number().int().describe("Error cells in the whole Result"),
  columns: z.array(z.string()),
  columnTypes: z
    .array(workbookColumnTypeSchema)
    .describe("Each column's Declared type, by position in columns"),
  validation: workbookResultValidationSchema,
  rows: z
    .array(z.record(z.string(), z.string()))
    .optional()
    .describe(
      "The Result's first rows as records of column name to the value as shown (TRUE/FALSE, error values like #N/A); only when rows were asked for",
    ),
});

/**
 * A Run Workbook node's Result: its schema, counts and Validation status,
 * never its data unless the node opts into a few sample rows. Downstream
 * steps read rows by `workbookRunId` + `sheetId` (Read Workbook Result).
 */
const runWorkbookNodeResultSchema = workbookRunResultSchema.extend({
  rows: z
    .array(z.record(z.string(), z.string()))
    .max(RUN_WORKBOOK_MAX_SAMPLE_ROWS)
    .optional()
    .describe(
      "Sample rows, only when the node includes sample rows (at most 10). Read the Result's rows with Read Workbook Result.",
    ),
});

const workbookRunFileSchema = z.object({
  outputId: z.uuid(),
  name: z.string(),
  fileName: z.string(),
  url: z
    .string()
    .nullable()
    .describe(
      "A download link valid for one hour, when the node exports files and this run filled every sheet; otherwise null",
    ),
});

export const workbookRunOutcomeSchema = z.object({
  workbookId: z.uuid(),
  workbookRunId: z.uuid(),
  status: z.enum(["completed", "failed"]),
  error: z.string().nullable().describe("The run failure, when it failed"),
  revision: z.number().int().describe("The saved revision the run executed"),
  results: z.array(workbookRunResultSchema),
  files: z.array(workbookRunFileSchema),
});

/**
 * Run Workbook: runs a saved Workbook to completion and returns what its
 * Results hold, like a notebook task in a job: never their rows unless asked,
 * so a step run stays small. Runs as the user who triggered the workflow
 * run, else the author of the workflow version, who must own the Workbook.
 */
export const runWorkbookStep = defineStepConfig({
  type: "run_workbook",
  name: "Run Workbook",
  description:
    "Run a saved Workbook to completion, optionally a pinned Version, with Parameters replacing its inline tables. Outputs the run status, each Worksheet's columns and types, row and error counts and validation status (no rows unless it includes sample rows), and the Workbook's output files.",
  category: "Data Transform",
  inputSchema: z.object({
    parameters: workbookParametersSchema.optional(),
  }),
  outputSchema: workbookRunOutcomeSchema.extend({
    results: z.array(runWorkbookNodeResultSchema),
  }),
  configSchema: z.object({
    workbookId: z.uuid().describe("The Workbook to run"),
    versionId: z
      .uuid()
      .optional()
      .describe(
        "A saved Version to run; the latest saved Workbook when omitted",
      ),
    exportFiles: z
      .boolean()
      .optional()
      .describe(
        "Also export each output file this run fills and output a download link to it",
      ),
    includeSampleRows: z
      .number()
      .int()
      .min(0)
      .max(RUN_WORKBOOK_MAX_SAMPLE_ROWS)
      .optional()
      .describe(
        "Rows of each Result to include in the output (default 0, at most 10); read more with Read Workbook Result",
      ),
    continueOnFailure: z
      .boolean()
      .optional()
      .describe(
        "When the run fails, output status failed instead of failing the step",
      ),
  }),
  requiredPermissions: [{ action: Action.Execute, subject: Subject.Workbook }],
  emits: [] as const,
  execution: {
    startToCloseTimeout: WORKBOOK_RUN_STEP_TIMEOUT,
    // A Run captures its inputs once; a retry would be a new Run.
    retry: { maximumAttempts: 1 },
  },
});

/**
 * Read Workbook Result: a Workbook Run's Result rows, for the step that truly
 * needs them after a Run Workbook node, which outputs none. Reads as the same
 * user a Run Workbook node runs as.
 */
export const readWorkbookResultStep = defineStepConfig({
  type: "read_workbook_result",
  name: "Read Workbook Result",
  description:
    "Read a Workbook run's Result rows: wire workbookId and workbookRunId from a Run Workbook node. Outputs one Worksheet's (or every Worksheet's) columns, types, counts, validation status and first rows (at most 100).",
  category: "Data Transform",
  inputSchema: z.object({
    workbookId: z.uuid().describe("The Workbook, from Run Workbook"),
    workbookRunId: z.uuid().describe("The run, from Run Workbook"),
  }),
  outputSchema: z.object({
    workbookId: z.uuid(),
    workbookRunId: z.uuid(),
    status: z.enum(["queued", "running", "completed", "failed"]),
    results: z.array(workbookRunResultSchema),
  }),
  configSchema: z.object({
    worksheet: z
      .string()
      .trim()
      .min(1)
      .optional()
      .describe("The Worksheet to read, by name; every Worksheet when omitted"),
    rowLimit: z
      .number()
      .int()
      .min(1)
      .max(WORKBOOK_PREVIEW_ROWS)
      .optional()
      .describe("Rows of each Result to read (default and at most 100)"),
  }),
  requiredPermissions: [{ action: Action.Read, subject: Subject.Workbook }],
  emits: [] as const,
});

/** Agent tool: the same Run as the node, choosing the Workbook per call. */
export const runSavedWorkbookTool = defineToolConfig({
  type: "run_saved_workbook",
  name: "Run Saved Workbook",
  description:
    "Run one of the user's saved Workbooks to completion and read its results. Use list_workbooks to find its id and parameters. Returns the run status, the failure when it failed, and each Worksheet's first rows.",
  category: "Data Transform",
  inputSchema: z.object({
    workbookId: z.uuid().describe("The Workbook to run, from list_workbooks"),
    versionId: z
      .uuid()
      .optional()
      .describe("A saved Version to run instead of the latest"),
    parameters: workbookParametersSchema.optional(),
    rowLimit: z
      .number()
      .int()
      .min(0)
      .max(WORKBOOK_PREVIEW_ROWS)
      .optional()
      .describe("Rows of each Result to return (default 20, at most 100)"),
  }),
  outputSchema: workbookRunOutcomeSchema,
  configSchema: z.object({}),
  requiredPermissions: [{ action: Action.Execute, subject: Subject.Workbook }],
  emits: [] as const,
});

/** Agent tool: the Workbooks the user can open, with their Parameters. */
export const listWorkbooksTool = defineToolConfig({
  type: "list_workbooks",
  name: "List Workbooks",
  description:
    "List the user's saved Workbooks with their worksheets, outputs and parameters (inline tables a run can replace).",
  category: "Data Transform",
  inputSchema: z.object({
    search: z
      .string()
      .optional()
      .describe("Only Workbooks whose name contains this text"),
  }),
  outputSchema: z.object({
    workbooks: z.array(
      z.object({
        workbookId: z.uuid(),
        name: z.string(),
        updatedAt: z.string(),
        worksheets: z.array(z.string()),
        outputs: z.array(z.string()),
        parameters: z.array(
          z.object({ name: z.string(), columns: z.array(z.string()) }),
        ),
      }),
    ),
  }),
  configSchema: z.object({}),
  requiredPermissions: [{ action: Action.Read, subject: Subject.Workbook }],
  emits: [] as const,
});

/** Agent tool: a past Run's status and Result rows. */
export const readWorkbookResultsTool = defineToolConfig({
  type: "read_workbook_results",
  name: "Read Workbook Results",
  description:
    "Read a Workbook run's status, failure and each Worksheet's first rows: the latest run unless workbookRunId names one.",
  category: "Data Transform",
  inputSchema: z.object({
    workbookId: z.uuid(),
    workbookRunId: z
      .uuid()
      .optional()
      .describe("A run of the Workbook; its latest run when omitted"),
    rowLimit: z
      .number()
      .int()
      .min(0)
      .max(WORKBOOK_PREVIEW_ROWS)
      .optional()
      .describe("Rows of each Result to return (default 20, at most 100)"),
  }),
  outputSchema: workbookRunOutcomeSchema
    .omit({ status: true, files: true })
    .extend({
      status: z.enum(["queued", "running", "completed", "failed"]),
      createdAt: z.string(),
    }),
  configSchema: z.object({}),
  requiredPermissions: [{ action: Action.Read, subject: Subject.Workbook }],
  emits: [] as const,
});

/** Agent tool: a Workbook's configuration, read off its page. */
export const describeWorkbookTool = defineToolConfig({
  type: "describe_workbook",
  name: "Describe Workbook",
  description:
    "Read how a saved Workbook works: its sources (what they read, not their rows), worksheets (columnar ones with formulas as Excel text, filters, lookups, sort; pivots with their rows, columns and values; each with its validations), outputs (xlsx files and reports), process instructions (its SOP), task rules, parameters and how its newest run ended.",
  category: "Data Transform",
  inputSchema: z.object({
    workbookId: z.uuid().describe("The Workbook, from list_workbooks"),
  }),
  outputSchema: z
    .object({
      workbookId: z.uuid(),
      name: z.string(),
      page: z.string().describe("Where the Workbook opens in the app"),
    })
    .loose(),
  configSchema: z.object({}),
  requiredPermissions: [{ action: Action.Read, subject: Subject.Workbook }],
  emits: [] as const,
});

/** Agent tool: an Output's download link, or where a Report is viewed. */
export const workbookOutputLinkTool = defineToolConfig({
  type: "workbook_output_link",
  name: "Workbook Output Link",
  description:
    "Get a saved Workbook output for the user: a file output's download link (xlsx, or csv for a one-tab file) built from its worksheets' newest current results, or, for a report, the page it is viewed and exported as PDF on. Refused while a file's worksheet has no current result or failed an error validation: run the Workbook first.",
  category: "Data Transform",
  inputSchema: z.object({
    workbookId: z.uuid(),
    outputId: z.uuid().describe("An output id, from describe_workbook"),
    format: z.enum(["xlsx", "csv"]).optional(),
  }),
  outputSchema: z.object({
    kind: z.enum(["file", "report"]),
    name: z.string(),
    page: z.string(),
    url: z.string().nullable(),
    note: z.string(),
  }),
  configSchema: z.object({}),
  requiredPermissions: [{ action: Action.Read, subject: Subject.Workbook }],
  emits: [] as const,
});

/** Agent tool: what automates a Workbook and the Tasks it opened. */
export const listWorkbookAutomationsTool = defineToolConfig({
  type: "list_workbook_automations",
  name: "List Workbook Automations",
  description:
    "List what automates a saved Workbook: workflows that run it or react to its runs, agents it is attached to, and the tasks its task rules and instructions opened (null when the user can't read tasks).",
  category: "Data Transform",
  inputSchema: z.object({ workbookId: z.uuid() }),
  outputSchema: z
    .object({ workflows: z.array(z.unknown()), agents: z.array(z.unknown()) })
    .loose(),
  configSchema: z.object({}),
  requiredPermissions: [{ action: Action.Read, subject: Subject.Workbook }],
  emits: [] as const,
});

/** Agent tool: applies a Workbook's Task rules to one of its completed Runs. */
export const openWorkbookRunTasksTool = defineToolConfig({
  type: "open_workbook_run_tasks",
  name: "Open Workbook Run Tasks",
  description:
    "Apply a Workbook's task rules to one of its completed runs, opening a task per row of each rule's worksheet (at most 100 per rule); rows that already have their task get none. Only when the user asks.",
  category: "Data Transform",
  inputSchema: z.object({
    workbookId: z.uuid(),
    workbookRunId: z.uuid().describe("A completed run of the Workbook"),
  }),
  outputSchema: z.object({
    rules: z.array(
      z.object({
        taskRuleId: z.uuid(),
        opened: z.number().int(),
        existing: z.number().int(),
        truncated: z.boolean(),
        skipped: z.enum(["worksheet-removed", "not-computed"]).nullable(),
      }),
    ),
  }),
  configSchema: z.object({}),
  requiredPermissions: [
    { action: Action.Read, subject: Subject.Workbook },
    { action: Action.Create, subject: Subject.Task },
  ],
  emits: [] as const,
});
